// Lá số Tử Vi — core dùng chung (types, helpers, CungCell, InfoRow, buildViewFromApi)
// + component tĩnh TuviLaSoBoard. Tách từ routes/tu-vi.tsx để tu-vi / tong-hop
// dùng chung (DRY). Phần này KHÔNG chứa state tương tác của trang.
import { useRef, useState, useEffect } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import {
  TUVI_CUNG_KEY_BY_NAME,
  type TuviChart,
  type TuviChartLuuNien,
  type TuviCung,
  type TuviDaiVan,
} from "@/lib/boide-api";

// Thứ tự 12 địa chi (Tý=0 … Hợi=11) — khớp chi_idx ở BE.
const CHI_ORDER = [
  "Tý",
  "Sửu",
  "Dần",
  "Mão",
  "Thìn",
  "Tỵ",
  "Ngọ",
  "Mùi",
  "Thân",
  "Dậu",
  "Tuất",
  "Hợi",
];

const CUNG_NAMES = [
  "Mệnh",
  "Phụ Mẫu",
  "Phúc Đức",
  "Điền Trạch",
  "Quan Lộc",
  "Nô Bộc",
  "Thiên Di",
  "Tật Ách",
  "Tài Bạch",
  "Tử Tức",
  "Phu Thê",
  "Huynh Đệ",
];

// 12 địa chi mapped to 4x4 grid positions (row, col), 1-indexed
const CHI_POSITIONS: { chi: string; row: number; col: number }[] = [
  { chi: "Tỵ", row: 1, col: 1 },
  { chi: "Ngọ", row: 1, col: 2 },
  { chi: "Mùi", row: 1, col: 3 },
  { chi: "Thân", row: 1, col: 4 },
  { chi: "Thìn", row: 2, col: 1 },
  { chi: "Dậu", row: 2, col: 4 },
  { chi: "Mão", row: 3, col: 1 },
  { chi: "Tuất", row: 3, col: 4 },
  { chi: "Dần", row: 4, col: 1 },
  { chi: "Sửu", row: 4, col: 2 },
  { chi: "Tý", row: 4, col: 3 },
  { chi: "Hợi", row: 4, col: 4 },
];

type Element = "metal" | "wood" | "water" | "fire" | "earth";

type Star = { name: string; element: Element; rank: "chinh" | "phu" | "tieu"; trangThai?: string };

const CHINH_TINH: Star[] = [
  { name: "Tử Vi", element: "earth", rank: "chinh" },
  { name: "Thiên Cơ", element: "wood", rank: "chinh" },
  { name: "Thái Dương", element: "fire", rank: "chinh" },
  { name: "Vũ Khúc", element: "metal", rank: "chinh" },
  { name: "Thiên Đồng", element: "water", rank: "chinh" },
  { name: "Liêm Trinh", element: "fire", rank: "chinh" },
  { name: "Thiên Phủ", element: "earth", rank: "chinh" },
  { name: "Thái Âm", element: "water", rank: "chinh" },
  { name: "Tham Lang", element: "wood", rank: "chinh" },
  { name: "Cự Môn", element: "water", rank: "chinh" },
  { name: "Thiên Tướng", element: "water", rank: "chinh" },
  { name: "Thiên Lương", element: "earth", rank: "chinh" },
  { name: "Thất Sát", element: "metal", rank: "chinh" },
  { name: "Phá Quân", element: "water", rank: "chinh" },
];

const PHU_TINH: Star[] = [
  { name: "Tả Phụ", element: "earth", rank: "phu" },
  { name: "Hữu Bật", element: "water", rank: "phu" },
  { name: "Văn Xương", element: "metal", rank: "phu" },
  { name: "Văn Khúc", element: "water", rank: "phu" },
  { name: "Thiên Khôi", element: "fire", rank: "phu" },
  { name: "Thiên Việt", element: "fire", rank: "phu" },
  { name: "Lộc Tồn", element: "earth", rank: "phu" },
  { name: "Hóa Lộc", element: "earth", rank: "phu" },
  { name: "Hóa Quyền", element: "wood", rank: "phu" },
  { name: "Hóa Khoa", element: "water", rank: "phu" },
];

// Cát tinh (sao tốt) — for left column
const TIEU_CAT: string[] = [
  "Đào Hoa",
  "Hồng Loan",
  "Thiên Hỷ",
  "Long Đức",
  "Phượng Các",
  "Tam Thai",
  "Bát Tọa",
  "Ân Quang",
  "Thiên Quý",
  "Quốc Ấn",
  "Thai Phụ",
  "Phong Cáo",
  "Thiên Mã",
];
// Hung/Sát tinh (sao xấu) — for right column
const TIEU_HUNG: string[] = [
  "Kình Dương",
  "Đà La",
  "Địa Không",
  "Địa Kiếp",
  "Hỏa Tinh",
  "Linh Tinh",
  "Thiên Khốc",
  "Thiên Hư",
  "Thiên Hình",
  "Thiên Diêu",
  "Cô Thần",
  "Quả Tú",
];
const TIEU_TINH = [...TIEU_CAT, ...TIEU_HUNG];
const TUAN_TRIET = ["Tuần", "Triệt", null, null, null] as const;

// Thiên can short letter (matches reference: Đ.Tỵ, M.Ngọ, K.Mùi…)
const CAN_SHORT: Record<string, string> = {
  Giáp: "G",
  Ất: "Ấ",
  Bính: "B",
  Đinh: "Đ",
  Mậu: "M",
  Kỷ: "K",
  Canh: "C",
  Tân: "T",
  Nhâm: "N",
  Quý: "Q",
};

// Tiểu hạn index (T.1..T.12) — vòng cố định bắt đầu từ Sửu
const TIEU_HAN_INDEX: Record<string, number> = {
  Sửu: 1,
  Dần: 2,
  Mão: 3,
  Thìn: 4,
  Tỵ: 5,
  Ngọ: 6,
  Mùi: 7,
  Thân: 8,
  Dậu: 9,
  Tuất: 10,
  Hợi: 11,
  Tý: 12,
};

function elementClass(e: Element): string {
  return {
    metal: "text-element-metal",
    wood: "text-element-wood",
    water: "text-element-water",
    fire: "text-element-fire",
    earth: "text-element-earth",
  }[e];
}

// Deterministic pseudo-random from string
function hash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function mulberry32(seed: number) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type ColumnStar = {
  name: string;
  loai: string;
  element: Element;
  trangThai?: string;
};

type CungData = {
  chi: string;
  cung: string;
  row: number;
  col: number;
  thienCan?: string;
  isMenh?: boolean;
  isThan?: boolean;
  daiVanTuoi?: string | null;
  tieuVanNam?: string | null;
  chinh: Star[];
  phu: Star[];
  cat: string[];
  hung: string[];
  tieuAll: { name: string; trangThai?: string }[];
  columnStars: ColumnStar[];
  vongTruongSinh: { name: string; element: Element }[];
  tuanTriet: string | null;
};

function generateChart(seed: string): {
  menh: string;
  cuc: string;
  nguHanh: string;
  cungs: CungData[];
  apiMeta?: ApiMeta;
  daiVan: TuviDaiVan[];
} {
  const rng = mulberry32(hash(seed || "mac-dinh"));
  const pick = <T,>(arr: T[]) => arr[Math.floor(rng() * arr.length)];

  // rotate cung names starting from a random index → menh placement
  const menhIdx = Math.floor(rng() * 12);
  const cungs: CungData[] = CHI_POSITIONS.map((p, i) => {
    const cungName = CUNG_NAMES[(i - menhIdx + 12) % 12];
    const nChinh = rng() < 0.7 ? (rng() < 0.4 ? 2 : 1) : 0;
    const chinh: Star[] = [];
    const used = new Set<string>();
    for (let k = 0; k < nChinh; k++) {
      let s = pick(CHINH_TINH);
      while (used.has(s.name)) s = pick(CHINH_TINH);
      used.add(s.name);
      chinh.push(s);
    }
    const nPhu = Math.floor(rng() * 3);
    const phu: Star[] = [];
    const usedPhu = new Set<string>();
    for (let k = 0; k < nPhu; k++) {
      let s = pick(PHU_TINH);
      while (usedPhu.has(s.name)) s = pick(PHU_TINH);
      usedPhu.add(s.name);
      phu.push(s);
    }
    const nCat = Math.floor(rng() * 3);
    const cat: string[] = [];
    const usedC = new Set<string>();
    for (let k = 0; k < nCat; k++) {
      let s = pick(TIEU_CAT);
      while (usedC.has(s)) s = pick(TIEU_CAT);
      usedC.add(s);
      cat.push(s);
    }
    const nHung = Math.floor(rng() * 3);
    const hung: string[] = [];
    const usedH = new Set<string>();
    for (let k = 0; k < nHung; k++) {
      let s = pick(TIEU_HUNG);
      while (usedH.has(s)) s = pick(TIEU_HUNG);
      usedH.add(s);
      hung.push(s);
    }
    const tuanTriet = rng() < 0.18 ? (rng() < 0.5 ? "TUẦN" : "TRIỆT") : null;
    return {
      ...p,
      cung: cungName,
      chinh,
      phu,
      cat,
      hung,
      tieuAll: [...cat, ...hung].map((n) => ({ name: n })),
      columnStars: [
        ...phu.map((s) => ({
          name: s.name,
          loai: "phu_tinh",
          element: s.element,
          trangThai: s.trangThai,
        })),
        ...cat.map((name) => ({ name, loai: "cat_tinh", element: "wood" as Element })),
        ...hung.map((name) => ({ name, loai: "sat_tinh", element: "fire" as Element })),
      ],
      vongTruongSinh: [],
      tuanTriet,
      isMenh: cungName === "Mệnh",
    };
  });

  const menhCung = cungs.find((c) => c.cung === "Mệnh")!;
  const cucs = ["Thủy Nhị Cục", "Mộc Tam Cục", "Kim Tứ Cục", "Thổ Ngũ Cục", "Hỏa Lục Cục"];
  const elements = ["Kim", "Mộc", "Thủy", "Hỏa", "Thổ"];
  return {
    menh: menhCung.chinh[0]?.name ?? "Vô Chính Diệu",
    cuc: cucs[Math.floor(rng() * cucs.length)],
    nguHanh: elements[Math.floor(rng() * elements.length)],
    cungs,
    daiVan: [],
  };
}

type ApiMeta = {
  amDuong?: string;
  canNam?: string;
  chiNam?: string;
  canThang?: string;
  chiThang?: string;
  canNgay?: string;
  chiNgay?: string;
  canGio?: string;
  chiGio?: string;
  soCuc?: number;
  canLuong?: string;
  menhCucRelation?: string;
  chuMenh?: string;
  chuThan?: string;
  laiNhanCung?: string;
  namXem?: number;
  canNamXem?: string;
  chiNamXem?: string;
  namAm?: number;
  thangAm?: number;
  ngayAm?: number;
};

type ChartView = ReturnType<typeof generateChart>;

function normalizeElementInput(value?: string): string {
  return (value || "").trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function mauToElement(mau?: string): Element {
  switch (normalizeElementInput(mau)) {
    case "red":
    case "fire":
      return "fire";
    case "white":
    case "metal":
      return "metal";
    case "blue":
    case "black":
    case "water":
      return "water";
    case "green":
    case "wood":
      return "wood";
    default:
      return "earth";
  }
}

function nguHanhToElement(nguHanh?: string): Element | undefined {
  const value = normalizeElementInput(nguHanh).replace(/^hanh[_\s-]?/, "");
  switch (value) {
    case "kim":
    case "metal":
      return "metal";
    case "moc":
    case "wood":
      return "wood";
    case "thuy":
    case "water":
      return "water";
    case "hoa":
    case "fire":
      return "fire";
    case "tho":
    case "earth":
      return "earth";
    default:
      return undefined;
  }
}

function starElement(nguHanh?: string, mau?: string): Element {
  return nguHanhToElement(nguHanh) ?? mauToElement(mau);
}

function normalizeStarName(name: string): string {
  return name
    .trim()
    .normalize("NFC")
    .replace(/Hoá/g, "Hóa")
    .replace(/Hoả/g, "Hỏa")
    .replace(/Riêu/g, "Diêu");
}

const HUNG_LOOKUP = new Set(TIEU_HUNG.map(normalizeStarName));
const RIGHT_COLUMN_STARS = new Set(
  [
    "Phi Liêm",
    "Trực Phù",
    "Phá Toái",
    "Thái Tuế",
    "Thiên Không",
    "Bệnh Phù",
    "Thiên Riêu",
    "Đại Hao",
    "Tang Môn",
    "Cô Thần",
    "Phục Binh",
    "Đà La",
    "Địa Kiếp",
    "Hóa Kỵ",
    "Hoá Kỵ",
    "Quan Phủ",
    "Quan Phù",
    "Địa Võng",
    "Tử Phù",
    "Kiếp Sát",
    "Đẩu Quân",
    "Lưu Hà",
    "Kình Dương",
    "Địa Không",
    "Hoả Tinh",
    "Hỏa Tinh",
    "Tuế Phá",
    "Thiên Hư",
    "Thiên Khốc",
    "Tiểu Hao",
    "Bạch Hổ",
    "Thiên Thương",
    "Tướng Quân",
    "Thiên Hình",
    "Linh Tinh",
    "Điếu Khách",
    "Quả Tú",
    "Thiên La",
    "Thiên Sứ",
  ].map(normalizeStarName),
);
const LEFT_COLUMN_PRIORITY = new Set(
  [
    "Thiên Việt",
    "Thai Phụ",
    "Hỷ Thần",
    "Thiên Phúc",
    "Hóa Lộc",
    "Hoá Lộc",
    "Thiếu Dương",
    "Thiên Y",
    "Quốc Ấn",
    "Thiên Mã",
    "Thiếu Âm",
    "Hồng Loan",
    "Thiên Trù",
    "Tả Phù",
    "Hóa Khoa",
    "Hoá Khoa",
    "Long Trì",
    "Thiên Quan",
    "Hoa Cái",
    "Văn Xương",
    "Lộc Tồn",
    "Bác Sĩ",
    "Nguyệt Đức",
    "Lực Sĩ",
    "Bát Tọa",
    "Thiên Quý",
    "Thanh Long",
    "Long Đức",
    "Thiên Thọ",
    "Phong Cáo",
    "Địa Giải",
    "Hóa Quyền",
    "Hoá Quyền",
    "Tam Thai",
    "Ân Quang",
    "Văn Tinh",
    "Thiên Giải",
    "Văn Khúc",
    "Thiên Khôi",
    "Phúc Đức",
    "Thiên Đức",
    "Thiên Tài",
    "Thiên Hỷ",
    "Đào Hoa",
    "Tấu Thư",
    "Phượng Các",
    "Hữu Bật",
    "Giải Thần",
    "Đường Phù",
  ].map(normalizeStarName),
);
const STAR_TYPE_ORDER: Record<string, number> = {
  tu_hoa: 0,
  cat_tinh: 1,
  vong_thai_tue: 2,
  trung_tinh: 3,
  phu_tinh: 4,
  sat_tinh: 5,
  luu_nien: 6,
};

// Sao lưu niên mang tiền tố "Lưu " — bỏ tiền tố để tra cứu/định cột như sao gốc cùng tên.
function baseStarName(name: string): string {
  const n = normalizeStarName(name);
  return n.startsWith("Lưu ") ? n.slice("Lưu ".length).trim() : n;
}

function isRightColumnStar(star: ColumnStar): boolean {
  const name = baseStarName(star.name);
  return star.loai === "sat_tinh" || HUNG_LOOKUP.has(name) || RIGHT_COLUMN_STARS.has(name);
}

function isLeftColumnStar(star: ColumnStar): boolean {
  return !isRightColumnStar(star);
}

function sortColumnStars(stars: ColumnStar[]): ColumnStar[] {
  return stars
    .slice()
    .sort((a, b) => (STAR_TYPE_ORDER[a.loai] ?? 99) - (STAR_TYPE_ORDER[b.loai] ?? 99));
}

function shortTrangThai(trangThai?: string): string | undefined {
  return trangThai?.slice(0, 1);
}

function formatCanChi(can?: string, chi?: string): string | undefined {
  return can && chi ? `${can} ${chi}` : undefined;
}

function formatHour(time: string): string {
  const [hour, minute] = time.split(":");
  const hourText = Number(hour || 0);
  const minuteText = Number(minute || 0);
  return minuteText ? `${hourText} giờ ${minuteText} phút` : `${hourText} giờ`;
}

function formatNamXem(meta?: ApiMeta): string | undefined {
  if (!meta?.namXem) return undefined;
  const canChi = formatCanChi(meta.canNamXem, meta.chiNamXem);
  const tuoi = meta.namAm ? meta.namXem - meta.namAm + 1 : undefined;
  return `${canChi ? `${canChi} ` : ""}(${meta.namXem})${tuoi ? `, ${tuoi} tuổi` : ""}`;
}

/**
 * Overlay sao lưu niên lên cung_list (client-side), trả về list MỚI.
 * Màu + ngũ hành của sao lưu lấy theo sao gốc cùng tên đã có sẵn trong lá số
 * (vd "Lưu Lộc Tồn" lấy màu của "Lộc Tồn") — không cần bảng tra riêng ở FE.
 */
function overlayLuuNien(cungList: TuviCung[], luuNien?: TuviChartLuuNien | null): TuviCung[] {
  if (!luuNien?.sao || !Object.keys(luuNien.sao).length) return cungList;
  // name (đã normalize) -> {mau, ngu_hanh} từ các sao gốc đang có trên lá số.
  const lookup = new Map<string, { mau?: string; ngu_hanh?: string }>();
  for (const c of cungList) {
    for (const s of c.sao) {
      lookup.set(normalizeStarName(s.ten), { mau: s.mau, ngu_hanh: s.ngu_hanh });
    }
  }
  const byIdx = new Map<number, string[]>();
  for (const [ten, idx] of Object.entries(luuNien.sao)) {
    const arr = byIdx.get(idx) ?? [];
    arr.push(ten);
    byIdx.set(idx, arr);
  }
  return cungList.map((c) => {
    const idx = CHI_ORDER.indexOf(c.chi);
    const names = byIdx.get(idx);
    if (!names?.length) return c;
    const luuStars = names.map((ten) => {
      const base = lookup.get(baseStarName(ten));
      return {
        ten,
        loai: "luu_nien",
        mau: base?.mau,
        trang_thai: undefined,
        ngu_hanh: base?.ngu_hanh,
      };
    });
    return { ...c, sao: [...c.sao, ...luuStars] };
  });
}

function buildViewFromApi(api: TuviChart): ChartView {
  const cungSource = api.luu_nien ? overlayLuuNien(api.cung_list, api.luu_nien) : api.cung_list;
  const cungs: CungData[] = CHI_POSITIONS.map((p) => {
    const apiCung = cungSource.find((c) => c.chi === p.chi);
    const sao = apiCung?.sao ?? [];
    const chinh: Star[] = sao
      .filter((s) => s.loai === "chinh_tinh")
      .map((s) => ({
        name: s.ten,
        element: starElement(s.ngu_hanh, s.mau),
        rank: "chinh" as const,
        trangThai: s.trang_thai,
      }));
    // Tuần / Triệt
    const tuanTrietStars = sao
      .filter((s) => s.loai === "tuan_triet")
      .map((s) => {
        const name = s.ten.toUpperCase();
        if (name.includes("TUẦN")) return "TUẦN";
        if (name.includes("TRIỆT")) return "TRIỆT";
        return name;
      });
    const tuanTriet = tuanTrietStars.length ? tuanTrietStars.join(" · ") : null;
    // Vòng Tràng Sinh
    const vongTruongSinh = sao
      .filter((s) => s.loai === "vong_truong_sinh")
      .map((s) => ({ name: s.ten, element: starElement(s.ngu_hanh, s.mau) }));
    // Phụ tinh = mọi sao còn lại (cát, sát, tứ hóa, trung tinh, vòng thái tuế, lưu niên)
    const phuLoais = new Set([
      "phu_tinh",
      "cat_tinh",
      "sat_tinh",
      "tu_hoa",
      "trung_tinh",
      "vong_thai_tue",
      "luu_nien",
    ]);
    const phu: Star[] = sao
      .filter((s) => phuLoais.has(s.loai))
      .map((s) => ({
        name: s.ten,
        element: starElement(s.ngu_hanh, s.mau),
        rank: "phu" as const,
        trangThai: s.trang_thai,
      }));
    const tieu = sao.filter((s) => phuLoais.has(s.loai));
    const columnStars: ColumnStar[] = tieu.map((s) => ({
      name: s.ten,
      loai: s.loai,
      element: starElement(s.ngu_hanh, s.mau),
      trangThai: s.trang_thai,
    }));
    const cat = columnStars.filter(isLeftColumnStar).map((s) => s.name);
    const hung = columnStars.filter(isRightColumnStar).map((s) => s.name);
    return {
      chi: p.chi,
      row: p.row,
      col: p.col,
      cung: apiCung?.ten_cung || "—",
      thienCan: apiCung?.thien_can,
      isMenh: !!apiCung?.is_menh,
      isThan: !!apiCung?.is_than,
      daiVanTuoi: apiCung?.dai_van_tuoi ?? null,
      tieuVanNam: apiCung?.tieu_van_nam ?? null,
      chinh,
      phu,
      cat,
      hung,
      tieuAll: tieu.map((s) => ({ name: s.ten, trangThai: s.trang_thai })),
      columnStars,
      vongTruongSinh,
      tuanTriet,
    };
  });
  const menhCung = cungs.find((c) => c.isMenh) ?? cungs.find((c) => c.cung === "Mệnh");
  return {
    menh: menhCung?.chinh[0]?.name ?? api.chu_menh ?? "Vô Chính Diệu",
    cuc: api.menh_cuc ?? "—",
    nguHanh: api.ban_menh ?? "—",
    cungs,
    daiVan: api.dai_van ?? [],
    apiMeta: {
      amDuong: api.am_duong,
      canNam: api.can_nam,
      chiNam: api.chi_nam,
      canThang: api.can_thang,
      chiThang: api.chi_thang,
      canNgay: api.can_ngay,
      chiNgay: api.chi_ngay,
      canGio: api.can_gio,
      chiGio: api.chi_gio,
      soCuc: api.so_cuc,
      canLuong: api.can_luong,
      menhCucRelation: api.menh_cuc_relation,
      chuMenh: api.chu_menh,
      chuThan: api.chu_than,
      laiNhanCung: api.lai_nhan_cung,
      namXem: api.nam_xem,
      canNamXem: api.can_nam_xem,
      chiNamXem: api.chi_nam_xem,
      namAm: api.nam_am,
      thangAm: api.thang_am,
      ngayAm: api.ngay_am,
    },
  };
}

const TAM_HOP: Record<string, string[]> = {
  Thân: ["Tý", "Thìn"],
  Tý: ["Thân", "Thìn"],
  Thìn: ["Thân", "Tý"],
  Tỵ: ["Dậu", "Sửu"],
  Dậu: ["Tỵ", "Sửu"],
  Sửu: ["Tỵ", "Dậu"],
  Dần: ["Ngọ", "Tuất"],
  Ngọ: ["Dần", "Tuất"],
  Tuất: ["Dần", "Ngọ"],
  Hợi: ["Mão", "Mùi"],
  Mão: ["Hợi", "Mùi"],
  Mùi: ["Hợi", "Mão"],
};
// Xung chiếu: cặp đối diện
const XUNG_CHIEU: Record<string, string> = {
  Tý: "Ngọ",
  Ngọ: "Tý",
  Sửu: "Mùi",
  Mùi: "Sửu",
  Dần: "Thân",
  Thân: "Dần",
  Mão: "Dậu",
  Dậu: "Mão",
  Thìn: "Tuất",
  Tuất: "Thìn",
  Tỵ: "Hợi",
  Hợi: "Tỵ",
};

// Center of cell (col, row 1-indexed) in 0..100 coords
function cellCenter(row: number, col: number) {
  return { x: (col - 0.5) * 25, y: (row - 0.5) * 25 };
}

function CungCell({
  data,
  cellIndex,
  chartKey,
  flashKey,
  focus,
  pinned,
  dim,
  onHoverIn,
  onHoverOut,
  onClick,
  score,
  isListView = false,
  noTooltip = false,
}: {
  data: CungData;
  cellIndex: number;
  chartKey: number;
  flashKey: number;
  focus: boolean;
  pinned: boolean;
  dim: boolean;
  onHoverIn: () => void;
  onHoverOut: () => void;
  onClick: () => void;
  score?: number;
  isListView?: boolean;
  noTooltip?: boolean;
}) {
  const isMystic = focus || pinned;
  const summaries: Record<string, string> = {
    Mệnh: "Bản chất, khí chất, cốt cách — soi rọi toàn bộ vận trình một đời.",
    "Phụ Mẫu": "Cha mẹ, huyết thống, phúc trạch tổ tiên để lại.",
    "Phúc Đức": "Tâm thức, hạnh phúc nội tại, phúc báo tích lũy.",
    "Điền Trạch": "Nhà cửa, đất đai, tài sản cố định, môi trường sống.",
    "Quan Lộc": "Sự nghiệp, công danh, chức vị và đường thăng tiến.",
    "Nô Bộc": "Bằng hữu, người dưới quyền, mối quan hệ xã hội.",
    "Thiên Di": "Di chuyển, xuất ngoại, vận động ngoài quê hương.",
    "Tật Ách": "Sức khỏe, bệnh tật, trắc trở thân thể.",
    "Tài Bạch": "Tiền bạc, tài lộc, khả năng tích lũy của cải.",
    "Tử Tức": "Con cái, hậu duệ, mối quan hệ thế hệ sau.",
    "Phu Thê": "Hôn nhân, bạn đời, duyên phận lứa đôi.",
    "Huynh Đệ": "Anh chị em, đồng môn, sự tương trợ ngang hàng.",
  };

  const tooltipClass = (() => {
    let pos =
      "absolute z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200 w-64 bg-card/95 border border-primary/50 backdrop-blur-xl p-3.5 rounded-xl shadow-gold text-xs font-normal text-foreground normal-case whitespace-normal leading-relaxed";

    // Vertical position
    if (data.row === 1) {
      pos += " top-full mt-2";
    } else {
      pos += " bottom-full mb-2";
    }

    // Horizontal position
    if (data.col === 1) {
      pos += " left-0 origin-top-left";
    } else if (data.col === 4) {
      pos += " right-0 origin-top-right";
    } else {
      pos += " left-1/2 -translate-x-1/2 origin-top";
    }

    return pos;
  })();

  const canShort = data.thienCan ? (CAN_SHORT[data.thienCan] ?? data.thienCan.charAt(0)) : "";
  const dvStart = data.daiVanTuoi ? data.daiVanTuoi.split(/[-–]/)[0]?.trim() : null;
  const leftStars = sortColumnStars(
    data.columnStars.filter((star) => isLeftColumnStar(star) && !isRightColumnStar(star)),
  );
  const leftStarNames = new Set(leftStars.map((star) => star.name));
  const rightStars = sortColumnStars(
    data.columnStars.filter((star) => !leftStarNames.has(star.name)),
  );
  const starClass = (star: ColumnStar) => {
    const nameLower = star.name.toLowerCase();
    const isPermanentLuu = nameLower === "lưu hà" || nameLower === "lưu niên văn tinh";
    const isLuuNien =
      (star.loai === "luu_nien" ||
        nameLower.startsWith("lưu ") ||
        nameLower.startsWith("l.") ||
        nameLower.startsWith("l. ")) &&
      !isPermanentLuu;
    return `${elementClass(star.element)}${isLuuNien ? " italic" : ""}`;
  };

  return (
    <button
      key={`${data.chi}-${flashKey}-${chartKey}`}
      data-cung-cell="true"
      onClick={onClick}
      onMouseEnter={onHoverIn}
      onMouseLeave={onHoverOut}
      style={{
        ...(isListView ? {} : { gridRow: data.row, gridColumn: data.col }),
        borderWidth: 1,
        ["--cell-i" as string]: cellIndex,
      }}
      className={`group relative z-0 flex h-full min-h-0 flex-col rounded-lg p-2 text-left transition border hover:z-50 focus-within:z-50 ${
        isListView
          ? "w-full min-h-[140px] p-4 text-[11px] leading-[1.4] bg-white/[0.035] hover:bg-white/[0.06] hover:border-primary/50"
          : "text-[10px] leading-[1.35] animate-cung-build bg-white/[0.025] hover:bg-white/[0.05] hover:border-primary/60"
      } ${
        pinned
          ? "border-primary/90 cung-pinned cung-mist"
          : focus
            ? "border-primary/80 cung-focus cung-mist"
            : dim
              ? "border-primary/10 bg-white/[0.015] cung-dim"
              : "border-primary/20 hover:border-primary/60"
      } ${!isListView && flashKey > 0 ? "animate-cell-flash" : ""}`}
    >
      {/* Tuần / Triệt badge */}
      {data.tuanTriet && (
        <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 z-10 rounded-sm bg-element-fire px-1.5 py-px font-display text-[8px] font-bold tracking-normal sm:tracking-[0.2em] text-background shadow-md whitespace-nowrap">
          {data.tuanTriet}
        </span>
      )}

      {/* Top row: can.chi | cung name (+Thân) | đại-vận tuổi bắt đầu */}
      <div className="flex items-center justify-between gap-1 border-b border-primary/15 pb-1 w-full">
        <span
          className={`font-serif-soft italic text-foreground/70 shrink-0 ${isListView ? "text-xs" : "text-[8.5px] sm:text-[9.5px]"}`}
        >
          {isListView && data.thienCan ? (
            <span className="text-primary/90">{data.thienCan} </span>
          ) : (
            canShort && <span className="text-primary/90">{canShort}.</span>
          )}
          {data.chi}
        </span>
        <span
          className={`flex min-w-0 items-center gap-0.5 sm:gap-1 font-display font-bold tracking-normal sm:tracking-[0.12em] text-primary ${isListView ? "text-[12px]" : "text-[8.5px] sm:text-[9.5px]"}`}
        >
          <span className="truncate">{data.cung.toUpperCase()}</span>
          {data.isThan && (
            <span className="rounded-sm bg-element-fire px-0.5 sm:px-1 py-px text-[7.5px] sm:text-[8px] font-bold tracking-normal text-background shrink-0 whitespace-nowrap">
              Thân
            </span>
          )}
          {data.tieuVanNam && (
            <span
              className="rounded-sm bg-primary px-0.5 sm:px-1 py-px text-[7.5px] sm:text-[8px] font-bold tracking-normal text-background shrink-0 whitespace-nowrap"
              title={`Tiểu hạn năm ${data.tieuVanNam}`}
            >
              T.Hạn
            </span>
          )}
        </span>
        <span
          className={`font-display font-semibold text-foreground/70 shrink-0 tabular-nums ${isListView ? "text-xs" : "text-[8.5px] sm:text-[9.5px]"}`}
        >
          {dvStart ? (isListView ? `ĐV: ${dvStart}` : dvStart) : ""}
        </span>
      </div>

      {/* Chính tinh — centered top */}
      <div className="mt-1 w-full flex flex-col items-center gap-[1px] text-center">
        {data.chinh.length > 0 ? (
          data.chinh.map((s) => (
            <span
              key={s.name}
              style={{ ["--cell-i" as string]: cellIndex }}
              className={`font-serif-soft font-bold leading-tight animate-chinh-twinkle ${isListView ? "text-[15px] my-0.5" : "text-[13px]"} ${elementClass(s.element)} ${isMystic ? "drop-shadow-[0_0_8px_currentColor]" : ""}`}
              title={s.trangThai || undefined}
            >
              {s.name}
              {s.trangThai && (
                <span className="ml-0.5 align-middle font-sans text-[9px] font-normal not-italic text-foreground/65">
                  ({s.trangThai})
                </span>
              )}
            </span>
          ))
        ) : (
          <span
            className={`font-serif-soft italic text-foreground/40 animate-chinh-twinkle ${isListView ? "text-xs my-0.5" : "text-[10.5px]"}`}
            style={{ ["--cell-i" as string]: cellIndex }}
          >
            Vô chính diệu
          </span>
        )}
      </div>

      {/* Phụ tinh + Tiểu tinh — trái cát/phụ, phải hung/sát */}
      {data.columnStars.length > 0 && (
        <div
          className={`mt-1.5 w-full grid grid-cols-2 gap-x-1.5 ${isListView ? "text-[10.5px] gap-y-0.5 border-t border-primary/5 pt-1.5" : "text-[9.5px]"} leading-snug grid`}
        >
          <div className="flex flex-col items-start gap-[1px]">
            {leftStars.map((star, i) => (
              <span
                key={`left-${star.name}-${i}`}
                className={`${starClass(star)} animate-phu-fade`}
                style={{ ["--star-i" as string]: i }}
              >
                {star.name}
                {shortTrangThai(star.trangThai) && (
                  <span className="ml-0.5 text-[8.5px] text-foreground/55">
                    ({shortTrangThai(star.trangThai)})
                  </span>
                )}
              </span>
            ))}
          </div>
          <div className="flex flex-col items-end gap-[1px] text-right">
            {rightStars.map((star, i) => (
              <span
                key={`right-${star.name}-${i}`}
                className={`${starClass(star)} animate-phu-fade`}
                style={{ ["--star-i" as string]: i + leftStars.length }}
              >
                {star.name}
                {shortTrangThai(star.trangThai) && (
                  <span className="ml-0.5 text-[8.5px] text-foreground/55">
                    ({shortTrangThai(star.trangThai)})
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer: chi | vòng Tràng Sinh | T.x */}
      <div className="mt-auto w-full grid grid-cols-[1fr_auto_1fr] items-end border-t border-primary/10 pt-1 text-[9px] tracking-wider grid">
        <span className="font-serif-soft italic text-foreground/60">{data.chi}</span>
        <span className="text-center font-medium" title="Vòng Tràng Sinh">
          {data.vongTruongSinh.map((star, i) => (
            <span key={`${star.name}-${i}`} className={elementClass(star.element)}>
              {i > 0 ? " · " : ""}
              {star.name}
            </span>
          ))}
        </span>
        <span className="text-right text-foreground/45 tabular-nums">
          {isListView ? "Hạn " : ""}T.{TIEU_HAN_INDEX[data.chi] ?? ""}
        </span>
      </div>

      {/* Hover Tooltip */}
      {!isListView && !noTooltip && (
        <div className={tooltipClass}>
          <div className="flex items-center justify-between gap-2 border-b border-primary/20 pb-1.5 mb-1.5">
            <span className="font-display font-bold tracking-[0.2em] text-primary">
              CUNG {data.cung.toUpperCase()}
            </span>
            {typeof score === "number" && (
              <span className="rounded-full bg-primary/20 border border-primary/40 px-2 py-0.5 text-[9px] font-semibold text-primary">
                {score}đ
              </span>
            )}
          </div>
          <p className="text-[11px] leading-relaxed text-foreground/80 font-normal">
            {summaries[data.cung] || "Chi tiết cung trên lá số."}
          </p>
          <div className="mt-2 text-[9px] text-muted-foreground italic text-right">
            Click để ghim & luận sâu
          </div>
        </div>
      )}
    </button>
  );
}

function InfoRow({
  label,
  value,
  extra,
  bold,
  accent,
}: {
  label: string;
  value: string;
  extra?: string;
  bold?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex flex-col border-b border-border/55 py-1 last:border-0 dark:border-primary/10 gap-0.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-foreground/62 dark:text-primary/75 shrink-0">
          {label}
        </span>
        <span
          className={`${bold ? "font-serif-soft text-[15px] font-semibold text-primary" : accent ? "font-medium text-foreground" : "text-foreground/90"} truncate`}
        >
          {value}
        </span>
      </div>
      {extra && (
        <span className="text-[10.5px] font-medium italic text-element-fire/90 text-right break-words">
          {extra}
        </span>
      )}
    </div>
  );
}

export {
  CUNG_NAMES,
  CHI_POSITIONS,
  CHINH_TINH,
  PHU_TINH,
  TIEU_CAT,
  TIEU_HUNG,
  TIEU_TINH,
  TUAN_TRIET,
  CAN_SHORT,
  TIEU_HAN_INDEX,
  elementClass,
  hash,
  mulberry32,
  generateChart,
  normalizeElementInput,
  mauToElement,
  nguHanhToElement,
  starElement,
  normalizeStarName,
  HUNG_LOOKUP,
  RIGHT_COLUMN_STARS,
  LEFT_COLUMN_PRIORITY,
  STAR_TYPE_ORDER,
  isRightColumnStar,
  isLeftColumnStar,
  sortColumnStars,
  shortTrangThai,
  formatCanChi,
  formatHour,
  formatNamXem,
  buildViewFromApi,
  overlayLuuNien,
  CHI_ORDER,
  TAM_HOP,
  XUNG_CHIEU,
  cellCenter,
  CungCell,
  InfoRow,
};
export type { Element, Star, ColumnStar, CungData, ApiMeta, ChartView };

/**
 * Lá số Tử Vi đầy đủ — bản TĨNH (không zoom/highlight/ghim). Nhận thẳng TuviChart
 * từ API, dùng ở những nơi chỉ cần xem (vd chat tổng hợp). Trông giống hệt lá số
 * trên trang Tử Vi.
 */
export function TuviLaSoBoard({
  apiChart,
  maskPii = false,
}: {
  apiChart: TuviChart;
  maskPii?: boolean;
}) {
  const chart = buildViewFromApi(apiChart);
  const name = maskPii ? "Ẩn danh" : apiChart.ho_ten || "Ẩn danh";
  const hourText = `${apiChart.gio_sinh} giờ`;
  const namXem = formatNamXem(chart.apiMeta);

  const chartViewportRef = useRef<HTMLDivElement>(null);
  const chartInnerRef = useRef<HTMLElement>(null);
  const [chartScale, setChartScale] = useState(1);
  const [chartHeight, setChartHeight] = useState<number | undefined>(undefined);
  const [chartZoomed, setChartZoomed] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const viewport = chartViewportRef.current;
    const inner = chartInnerRef.current;
    if (!viewport || !inner) return;

    const update = () => {
      const isMobile = window.matchMedia("(max-width: 1023px)").matches;
      const naturalWidth = 640;

      if (!isMobile || chartZoomed) {
        setChartScale(1);
        setChartHeight(undefined);
        return;
      }

      const available = viewport.clientWidth;
      const scale = available < naturalWidth ? available / naturalWidth : 1;
      setChartScale(scale);
      setChartHeight(scale < 1 ? inner.offsetHeight * scale : undefined);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(viewport);
    ro.observe(inner);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [chartZoomed]);

  return (
    <div className="w-full flex flex-col gap-2 min-w-0">
      {/* Zoom / ViewMode control for mobile */}
      <div className="flex justify-between items-center lg:hidden mb-1 px-1">
        <div className="flex rounded-full border border-primary/20 p-0.5 bg-background/50 backdrop-blur shadow-inner">
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className={`px-3 py-1.5 text-[10px] rounded-full font-serif-soft transition-all duration-300 cursor-pointer ${
              viewMode === "list"
                ? "bg-primary text-background font-semibold shadow-md"
                : "text-primary/70 hover:text-primary"
            }`}
          >
            Danh sách
          </button>
          <button
            type="button"
            onClick={() => setViewMode("grid")}
            className={`px-3 py-1.5 text-[10px] rounded-full font-serif-soft transition-all duration-300 cursor-pointer ${
              viewMode === "grid"
                ? "bg-primary text-background font-semibold shadow-md"
                : "text-primary/70 hover:text-primary"
            }`}
          >
            Lưới
          </button>
        </div>

        {viewMode === "grid" && (
          <button
            type="button"
            onClick={() => setChartZoomed(!chartZoomed)}
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-primary/30 bg-background/50 px-3.5 text-[11px] font-medium text-primary backdrop-blur transition active:scale-95 cursor-pointer"
          >
            {chartZoomed ? (
              <>
                <Minimize2 className="h-3.5 w-3.5" /> Thu nhỏ bản đồ
              </>
            ) : (
              <>
                <Maximize2 className="h-3.5 w-3.5" /> Xem đầy đủ (Phóng to)
              </>
            )}
          </button>
        )}
      </div>

      {viewMode === "list" ? (
        <div className="w-full flex flex-col gap-4 animate-cung-build">
          {/* Thiên bàn card at top of list */}
          <div className="w-full flex flex-col items-center justify-between rounded-3xl border border-gold-soft/45 bg-card/80 p-6 text-center shadow-[0_14px_34px_rgba(92,64,36,0.08)] backdrop-blur-sm dark:border-primary/20 dark:bg-card/40 dark:shadow-none">
            <div className="flex flex-col items-center gap-1">
              <span className="font-display text-[9px] tracking-[0.5em] text-primary/80">
                THIÊN BÀN
              </span>
              <h3 className="font-serif-soft text-2xl italic text-gold-gradient leading-none pr-2">
                LÁ SỐ TỬ VI
              </h3>
              <div className="h-px w-16 bg-primary/40" />
            </div>

            <div className="flex flex-1 w-full max-w-md flex-col gap-0.5 px-2 py-4 text-left text-[12px] mx-auto">
              <InfoRow label="Họ tên" value={name} bold />
              <InfoRow
                label="Năm (Âm lịch)"
                value={maskPii ? "•••" : `${chart.apiMeta?.namAm ?? apiChart.nam_sinh}`}
                extra={
                  maskPii ? undefined : formatCanChi(chart.apiMeta?.canNam, chart.apiMeta?.chiNam)
                }
              />
              <InfoRow
                label="Tháng (Âm lịch)"
                value={maskPii ? "•••" : `${chart.apiMeta?.thangAm ?? apiChart.thang_sinh}`}
                extra={
                  maskPii ? undefined : formatCanChi(chart.apiMeta?.canThang, chart.apiMeta?.chiThang)
                }
              />
              <InfoRow
                label="Ngày (Âm lịch)"
                value={maskPii ? "•••" : `${chart.apiMeta?.ngayAm ?? apiChart.ngay_sinh}`}
                extra={
                  maskPii ? undefined : formatCanChi(chart.apiMeta?.canNgay, chart.apiMeta?.chiNgay)
                }
              />
              <InfoRow
                label="Giờ"
                value={maskPii ? "•••" : hourText}
                extra={
                  maskPii ? undefined : formatCanChi(chart.apiMeta?.canGio, chart.apiMeta?.chiGio)
                }
              />
              {namXem && <InfoRow label="Năm xem" value={namXem} />}
              {chart.apiMeta?.amDuong && <InfoRow label="Âm dương" value={chart.apiMeta.amDuong} />}
              <InfoRow label="Bản mệnh" value={chart.nguHanh} accent />
              <InfoRow
                label="Mệnh cục"
                value={`${chart.cuc}${chart.apiMeta?.soCuc ? ` (${chart.apiMeta.soCuc})` : ""}`}
                extra={chart.apiMeta?.menhCucRelation}
                accent
              />
              <InfoRow label="Cân lượng" value={chart.apiMeta?.canLuong ?? "—"} />
              {chart.apiMeta?.chuMenh && <InfoRow label="Chủ mệnh" value={chart.apiMeta.chuMenh} />}
              {chart.apiMeta?.chuThan && <InfoRow label="Chủ thân" value={chart.apiMeta.chuThan} />}
              {chart.apiMeta?.laiNhanCung && (
                <InfoRow label="Lai nhân cung" value={chart.apiMeta.laiNhanCung} />
              )}
            </div>
          </div>

          {/* 12-cung vertical list/responsive grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(() => {
              const sortedCungs = [...chart.cungs].sort((a, b) => {
                const aIdx = CUNG_NAMES.indexOf(a.cung);
                const bIdx = CUNG_NAMES.indexOf(b.cung);
                return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
              });
              return sortedCungs.map((c) => {
                const cungScore = apiChart.scores?.cungs.find(
                  (sc) => sc.key === TUVI_CUNG_KEY_BY_NAME[c.cung],
                )?.score;
                const originalIndex = chart.cungs.findIndex((item) => item.chi === c.chi);
                return (
                  <CungCell
                    key={c.chi}
                    data={c}
                    cellIndex={originalIndex}
                    chartKey={0}
                    flashKey={0}
                    focus={false}
                    pinned={false}
                    dim={false}
                    score={cungScore}
                    onHoverIn={() => {}}
                    onHoverOut={() => {}}
                    onClick={() => {}}
                    isListView={true}
                  />
                );
              });
            })()}
          </div>
        </div>
      ) : (
        <div
          ref={chartViewportRef}
          style={{ height: chartHeight, maxHeight: chartZoomed ? "72vh" : undefined }}
          className={`w-full lg:h-auto! lg:max-h-none! lg:overflow-visible ${chartZoomed ? "overflow-auto scrollbar-thin" : "overflow-hidden"}`}
        >
          <div
            style={{ transform: `scale(${chartScale})`, transformOrigin: "top left" }}
            className="lg:transform-none!"
          >
            <section
              ref={chartInnerRef}
              className="relative min-w-[640px] lg:min-w-0 rounded-3xl border border-primary/20 bg-card/40 p-3 md:p-4 shadow-mystic backdrop-blur"
            >
              <div className="relative grid grid-cols-4 grid-rows-[repeat(4,minmax(140px,auto))] gap-1.5 md:gap-2">
                {chart.cungs.map((c, i) => {
                  const cungScore = apiChart.scores?.cungs.find(
                    (sc) => sc.key === TUVI_CUNG_KEY_BY_NAME[c.cung],
                  )?.score;
                  return (
                    <CungCell
                      key={c.chi}
                      data={c}
                      cellIndex={i}
                      chartKey={0}
                      flashKey={0}
                      focus={false}
                      pinned={false}
                      dim={false}
                      score={cungScore}
                      onHoverIn={() => {}}
                      onHoverOut={() => {}}
                      onClick={() => {}}
                    />
                  );
                })}

                {/* Thiên bàn center 2x2 */}
                <div className="col-start-2 col-end-4 row-start-2 row-end-4 flex flex-col items-center justify-between rounded-xl border border-gold-soft/45 bg-card/80 p-5 text-center shadow-[0_14px_34px_rgba(92,64,36,0.08)] backdrop-blur-sm dark:border-primary/20 dark:bg-background/30 dark:shadow-none">
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-display text-[9px] tracking-[0.2em] text-primary/80">
                      THIÊN BÀN
                    </span>
                    <h3 className="font-serif-soft text-2xl italic text-gold-gradient leading-none pr-2">
                      LÁ SỐ TỬ VI
                    </h3>
                    <div className="h-px w-16 bg-primary/40" />
                  </div>

                  <div className="flex flex-1 w-full flex-col gap-0.5 px-2 py-3 text-left text-[12px]">
                    <InfoRow label="Họ tên" value={name} bold />
                    <InfoRow
                      label="Năm (Âm lịch)"
                      value={maskPii ? "•••" : `${chart.apiMeta?.namAm ?? apiChart.nam_sinh}`}
                      extra={
                        maskPii ? undefined : formatCanChi(chart.apiMeta?.canNam, chart.apiMeta?.chiNam)
                      }
                    />
                    <InfoRow
                      label="Tháng (Âm lịch)"
                      value={maskPii ? "•••" : `${chart.apiMeta?.thangAm ?? apiChart.thang_sinh}`}
                      extra={
                        maskPii ? undefined : formatCanChi(chart.apiMeta?.canThang, chart.apiMeta?.chiThang)
                      }
                    />
                    <InfoRow
                      label="Ngày (Âm lịch)"
                      value={maskPii ? "•••" : `${chart.apiMeta?.ngayAm ?? apiChart.ngay_sinh}`}
                      extra={
                        maskPii ? undefined : formatCanChi(chart.apiMeta?.canNgay, chart.apiMeta?.chiNgay)
                      }
                    />
                    <InfoRow
                      label="Giờ"
                      value={maskPii ? "•••" : hourText}
                      extra={
                        maskPii ? undefined : formatCanChi(chart.apiMeta?.canGio, chart.apiMeta?.chiGio)
                      }
                    />
                    {namXem && <InfoRow label="Năm xem" value={namXem} />}
                    {chart.apiMeta?.amDuong && <InfoRow label="Âm dương" value={chart.apiMeta.amDuong} />}
                    <InfoRow label="Bản mệnh" value={chart.nguHanh} accent />
                    <InfoRow
                      label="Mệnh cục"
                      value={`${chart.cuc}${chart.apiMeta?.soCuc ? ` (${chart.apiMeta.soCuc})` : ""}`}
                      extra={chart.apiMeta?.menhCucRelation}
                      accent
                    />
                    <InfoRow label="Cân lượng" value={chart.apiMeta?.canLuong ?? "—"} />
                    {chart.apiMeta?.chuMenh && <InfoRow label="Chủ mệnh" value={chart.apiMeta.chuMenh} />}
                    {chart.apiMeta?.chuThan && <InfoRow label="Chủ thân" value={chart.apiMeta.chuThan} />}
                    {chart.apiMeta?.laiNhanCung && (
                      <InfoRow label="Lai nhân cung" value={chart.apiMeta.laiNhanCung} />
                    )}
                  </div>
                </div>
              </div>

              {/* Legend strip */}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-primary/15 pt-2 text-[10px] text-foreground/70">
                <div className="flex flex-wrap gap-x-3">
                  <span>
                    <b className="text-primary">M</b>: Miếu
                  </span>
                  <span>
                    <b className="text-primary">V</b>: Vượng
                  </span>
                  <span>
                    <b className="text-primary">Đ</b>: Đắc
                  </span>
                  <span>
                    <b className="text-primary">B</b>: Bình hòa
                  </span>
                  <span>
                    <b className="text-primary">H</b>: Hãm
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-3">
                  <span className="inline-flex items-center gap-1">
                    <i className="inline-block h-2.5 w-2.5 rounded-sm bg-element-metal/60" />
                    Kim
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="inline-block h-2.5 w-2.5 rounded-sm bg-element-wood/80" />
                    Mộc
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="inline-block h-2.5 w-2.5 rounded-sm bg-element-water/80" />
                    Thủy
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="inline-block h-2.5 w-2.5 rounded-sm bg-element-fire/80" />
                    Hỏa
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="inline-block h-2.5 w-2.5 rounded-sm bg-element-earth/80" />
                    Thổ
                  </span>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
